import { BehaviorSubject, Observable } from 'rxjs'
import { Injectable, OnDestroy } from '@angular/core'
import { LocalStorageService } from '@services/local-storage-service'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

@Injectable({
    providedIn: 'root',
})
export class ThemeService implements OnDestroy {
    private preferenceSubject: BehaviorSubject<ThemePreference>
    preference$: Observable<ThemePreference>

    private resolvedThemeSubject: BehaviorSubject<ResolvedTheme>
    resolvedTheme$: Observable<ResolvedTheme>

    private mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    private mediaQueryListener: (e: MediaQueryListEvent) => void

    constructor(
        private localStorageService: LocalStorageService
    ) {
        const preference = this.getStoredPreference()
        this.preferenceSubject = new BehaviorSubject<ThemePreference>(preference)
        this.preference$ = this.preferenceSubject.asObservable()

        const resolved = this.resolveTheme(preference)
        this.resolvedThemeSubject = new BehaviorSubject<ResolvedTheme>(resolved)
        this.resolvedTheme$ = this.resolvedThemeSubject.asObservable()
        this.applyTheme(resolved)

        this.mediaQueryListener = (e: MediaQueryListEvent) => {
            if (this.preferenceSubject.value === 'system') {
                const theme = e.matches ? 'dark' : 'light'
                this.resolvedThemeSubject.next(theme)
                this.applyTheme(theme)
            }
        }
        this.mediaQuery.addEventListener('change', this.mediaQueryListener)
    }

    ngOnDestroy() {
        this.mediaQuery.removeEventListener('change', this.mediaQueryListener)
    }

    setPreference(preference: ThemePreference) {
        this.localStorageService.setItem('themePreference', preference)
        this.preferenceSubject.next(preference)
        const resolved = this.resolveTheme(preference)
        this.resolvedThemeSubject.next(resolved)
        this.applyTheme(resolved)
    }

    get currentPreference(): ThemePreference {
        return this.preferenceSubject.value
    }

    private getStoredPreference(): ThemePreference {
        const stored = this.localStorageService.getItem('themePreference') as string | null
        switch (stored) {
        case 'light':
        case 'dark':
        case 'system':
            return stored
        default:
            return 'light'
        }
    }

    private resolveTheme(preference: ThemePreference): ResolvedTheme {
        if (preference === 'system') {
            return this.mediaQuery.matches ? 'dark' : 'light'
        }
        return preference
    }

    private applyTheme(theme: ResolvedTheme) {
        document.documentElement.setAttribute('data-bs-theme', theme)
    }
}
